/**
 * Hourly usage, grouped into the bars of the Overview's requests chart.
 *
 * The API returns only the hours that had traffic. A chart needs every slot in the range, empty ones
 * included, or a quiet night and a busy afternoon sit side by side as if nothing happened between
 * them. And it needs the slots in the viewer's local time: a day bar that starts at 03:00 because
 * that is UTC midnight is not the day anyone remembers.
 */

export interface HourlyUsage {
  hourStart: string;
  requests: number;
  cacheHits: number;
}

export type BucketSize = 'hour' | 'six-hours' | 'day';

export interface UsageBucket {
  start: Date;
  end: Date;
  requests: number;
  cacheHits: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** How long a range is, in whole days: 1, 7, 30 or 90 for the ranges the Overview offers. */
export function rangeDays(from: Date, to: Date): number {
  return Math.max(1, Math.round((to.getTime() - from.getTime()) / DAY_MS));
}

/**
 * One bar per hour for a day, per six hours for a week, per day beyond: enough bars to show the
 * shape of the traffic, few enough that each can still be pointed at.
 */
export function bucketSizeFor(days: number): BucketSize {
  if (days <= 1) return 'hour';
  if (days <= 7) return 'six-hours';
  return 'day';
}

/** The start of the slot containing `instant`, in local time. */
function bucketStart(instant: Date, size: BucketSize): Date {
  const start = new Date(instant);
  start.setMinutes(0, 0, 0);
  if (size === 'six-hours') start.setHours(start.getHours() - (start.getHours() % 6));
  if (size === 'day') start.setHours(0);
  return start;
}

/** Stepped in local time, so a day is a calendar day even across a daylight-saving change. */
function nextBucketStart(start: Date, size: BucketSize): Date {
  const next = new Date(start);
  if (size === 'hour') next.setHours(next.getHours() + 1);
  else if (size === 'six-hours') next.setHours(next.getHours() + 6);
  else next.setDate(next.getDate() + 1);
  return next;
}

/**
 * Every slot from the one containing `from` to the one containing `to`, with the hours summed into
 * them. The last slot is usually partial: it is the one happening now.
 */
export function bucketize(
  hourly: readonly HourlyUsage[],
  from: Date,
  to: Date,
  size: BucketSize,
): UsageBucket[] {
  const buckets: UsageBucket[] = [];
  for (
    let start = bucketStart(from, size);
    start.getTime() < to.getTime();
    start = nextBucketStart(start, size)
  ) {
    buckets.push({ start, end: nextBucketStart(start, size), requests: 0, cacheHits: 0 });
  }

  for (const row of hourly) {
    const at = new Date(row.hourStart).getTime();
    const bucket = buckets.find(
      (candidate) => candidate.start.getTime() <= at && at < candidate.end.getTime(),
    );
    if (bucket === undefined) continue;
    bucket.requests += row.requests;
    bucket.cacheHits += row.cacheHits;
  }

  return buckets;
}
